import React from 'react';
import { LAB_STATUS } from '../../../constants/lab';
import LabReportActions from '../../lab/LabReportActions';
import { formatDateTimeIST } from '../../../utils/formatIST';
import { HistorySection } from './HistorySection';

/**
 * VisitLabHistorySection — the lab orders (tests) on a visit, shown in history
 * with their status + the uploaded report (via LabReportActions's inline
 * viewer). This is what makes a completed LAB visit show its tests + report
 * instead of "No records for this visit."
 *
 * `reportOnly` (default false) forwards to `LabReportActions` — view/download
 * the report only, never Collect payment / Done outside. A caller working
 * off an already-loaded, non-refetchable snapshot (the patient card's F7
 * `OpdTimelineRow`) must pass this: without a live `onChanged` refetch, a
 * payment-collect or external-record action here would leave the on-screen
 * state stale after a real write.
 */
export function VisitLabHistorySection({ orders, loading, isDark, onChanged, reportOnly = false }) {
  if (loading) {
    return (
      <HistorySection title="🧪 Lab tests" isDark={isDark}>
        <p style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>Loading lab tests…</p>
      </HistorySection>
    );
  }
  if (!orders || orders.length === 0) return null;

  return (
    <HistorySection title="🧪 Lab tests" isDark={isDark}>
      {orders.map((o) => {
        const st = LAB_STATUS[o.status] || LAB_STATUS.advised;
        return (
          <div key={o.lab_order_id} style={{
            padding: '0.375rem 0.5rem', borderRadius: '0.375rem',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            marginBottom: '0.3125rem',
            display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: '0.5625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
              padding: '0.1rem 0.4rem', borderRadius: '9999px',
              background: isDark ? st.darkBg : st.bg, color: isDark ? st.darkFg : st.fg,
            }}>{st.label}</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#e8e9ed' : '#111827' }}>
              {o.test_name}
            </span>
            {o.specimen_type && (
              <span style={{ fontSize: '0.5625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{o.specimen_type}</span>
            )}
            {o.sample_number && (
              <span style={{
                fontSize: '0.5625rem', fontWeight: 600, letterSpacing: '0.02em',
                padding: '0.1rem 0.35rem', borderRadius: '0.25rem',
                background: isDark ? 'rgba(99,102,241,0.25)' : '#eef2ff',
                color: isDark ? '#c7d2fe' : '#4338ca',
                border: `1px solid ${isDark ? 'rgba(129,140,248,0.4)' : '#c7d2fe'}`,
              }}>🏷 {o.sample_number}</span>
            )}
            {o.final_cost != null && Number(o.final_cost) > 0 && (
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#5eead4' : '#0f766e' }}>
                ₹{Number(o.final_cost).toLocaleString('en-IN')}
              </span>
            )}
            {o.specimen_collected_at && (
              <span style={{ fontSize: '0.5625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                Collected {formatDateTimeIST(o.specimen_collected_at)}
              </span>
            )}
            {o.completed_at && (
              <span style={{ fontSize: '0.5625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                Completed {formatDateTimeIST(o.completed_at)}
              </span>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <LabReportActions order={o} onChanged={onChanged} reportOnly={reportOnly} />
            </div>
          </div>
        );
      })}
    </HistorySection>
  );
}
